/**
 * The pinned model catalogue (specs/models.md §3, §5.2–5.3, §13): what each scoring model IS. Static data;
 * the source of truth. `config/model-catalog.json` is generated from it for the deploy kit
 * (`npm run models:catalog`; `-- --check` exits 1 when the file differs).
 *
 * Adding a model = one more entry here (files with sizes and SHA-256, a recipe from the existing recipe
 * vocabulary, calibration constants, measured quality, defaults), `npm run models:catalog`, a deploy.
 * One record per entry exists in every database (`models` table, id = the entry's id).
 */

import type { FolderId, IconName, RuleId } from '../contracts/ids.js';
import type {
  EmbeddingCatalogEntry, ModelCatalog, ModelCatalogEntry, ModelId, ModelRun, RemoteJevCatalogEntry,
} from '../contracts/models.js';
import { E5_SMALL_MODEL_ID, JEV_MODEL_ID } from '../contracts/models.js';
import { JEV_MODEL } from '../contracts/scoring.js';
import type { GroupNode } from '../contracts/rules.js';

const folder = (id: string): FolderId => id as FolderId;
const EMPTY: GroupNode = { type: 'group', operator: 'all', children: [] };

/**
 * Jev's run rule of the recommended set-up (§13): it looks at what reaches Priority (a second opinion on the
 * on-device ladder) and at Cyrillic messages on arrival in Inbox, and scores on request.
 */
export const JEV_RECOMMENDED_RUN: ModelRun = {
  triggers: [{ on: 'entry', folderId: 'any', by: 'automatic' }, { on: 'request' }],
  condition: {
    type: 'group',
    operator: 'any',
    children: [
      { type: 'group', operator: 'all', children: [{ type: 'condition', field: 'folder', operator: 'in', values: [folder('priority')] }] },
      {
        type: 'group',
        operator: 'all',
        children: [
          { type: 'condition', field: 'folder', operator: 'in', values: [folder('inbox')] },
          { type: 'condition', field: 'script', operator: 'in', values: ['cyrillic'] },
        ],
      },
    ],
  },
};

/** The on-device model's run rule of the recommended set-up: everything that comes to rest in Inbox; on request. */
export const E5_RECOMMENDED_RUN: ModelRun = {
  triggers: [{ on: 'entry', folderId: folder('inbox'), by: 'automatic' }, { on: 'request' }],
  condition: EMPTY,
};

/** The 34 generic subjects of the anchored-share calibration, verbatim from the design-time probe (§5.2). */
export const E5_ANCHORS: readonly string[] = [
  'sports results and championships', 'celebrity news and entertainment', 'weather forecast and storms', 'cooking recipes and food',
  'stock markets and corporate earnings', 'elections and party politics', 'crime and court trials', 'health, medicine and diseases',
  'travel and tourism', 'movies and television series', 'music releases and concerts', 'fashion and lifestyle',
  'science discoveries and space', 'education and universities', 'religion and culture', 'war and military conflicts',
  'natural disasters and accidents', 'cars and transport', 'real estate and housing', 'jobs and the labour market',
  'consumer gadgets and phones', 'video games', 'social media trends', 'banking and personal finance', 'energy and climate change',
  'agriculture and the environment', 'art and literature', 'history and archaeology', 'local city news', 'business deals and mergers',
  'law and regulation', 'parenting and family', 'international diplomacy', 'immigration and borders',
];

const LANGUAGE_EXAMPLES = ['English', 'Serbian', 'Russian', 'German', 'French', 'Spanish', 'Italian', 'Portuguese', 'Ukrainian', 'Arabic', 'Chinese', 'Japanese', 'Hindi', 'Turkish'];

export const JEV_CATALOG_ENTRY: RemoteJevCatalogEntry = {
  id: JEV_MODEL_ID,
  kind: 'remote-jev',
  name: 'Jev 1.13',
  icon: 'sparkles' as IconName,
  description: 'Rates how well each new message fits each of your topics, 0–100, through Digester’s server. The actions on scoring then decide where it goes.',
  recommendation: 'Second opinion on Priority, and Cyrillic messages',
  version: JEV_MODEL,
  jevModel: JEV_MODEL,
  requires: 'aiScoring',
  languages: {
    label: 'Many languages',
    examples: ['English', 'Russian', 'Serbian'],
    note: 'Measured on the owner’s English and Russian feeds; the questions are asked in English.',
  },
  quality: {
    topicAuc: 0.95,
    topicAucNamesOnly: null,
    secondsPerMessage: 0.4,
    measuredOn: '247 hand-labelled messages of the owner’s feeds, topics as names, 2026-07 bake-off (jev-vs-semantics-test)',
    caveats: [
      'Sends each message’s title and the first characters of its text to the scorer through Digester’s server; about 850–950 tokens per message with 13 topics at the default 1,000 characters.',
      'Reads the topic name only: a topic’s description and paraphrases are not sent.',
      'Pro only; counted against your daily budget and your plan’s daily maximum.',
    ],
  },
  topicFields: {
    supported: ['name'],
    default: ['name'],
    limits: { nameChars: 80, descriptionChars: null, phrases: null, phraseChars: null },
  },
  defaults: {
    enabled: true,
    position: 0,
    run: JEV_RECOMMENDED_RUN,
    // bodyMaxChars 1,000: the quick-controls default the owner accepted (open-decisions.md 40), not the
    // design's 6,000; scoring.body_max_chars (6,000) stays the ceiling.
    options: { bodyMaxChars: 1000, topicMinScore: 15, dailyBudget: null, topicFields: ['name'] },
    ladder: [
      { ruleId: 'sr_priority' as RuleId, name: 'Strong match', icon: 'star' as IconName, minScore: 50, action: { type: 'move', folderId: folder('priority') } },
      { ruleId: 'sr_weak' as RuleId, name: 'Loose match', icon: 'signal' as IconName, minScore: 15, action: { type: 'move', folderId: folder('weak') } },
      { ruleId: 'sr_dismissed' as RuleId, name: 'Below the bar', icon: 'archive' as IconName, minScore: null, action: { type: 'move', folderId: folder('dismissed') } },
    ],
  },
};

const E5_REVISION = '761b726dd34fb83930e26aab4e9ac3899aa1fa78';

export const E5_SMALL_CATALOG_ENTRY: EmbeddingCatalogEntry = {
  id: E5_SMALL_MODEL_ID,
  kind: 'onnx-embedding',
  name: 'Multilingual E5 Small',
  icon: 'globe' as IconName,
  description: 'Matches messages to your topics on this device, across languages. Free, private, no tokens.',
  recommendation: 'Recommended for everyday reading',
  license: 'MIT',
  version: 'q8-761b726d-c1',
  requires: null,
  languages: {
    label: '100 languages',
    examples: LANGUAGE_EXAMPLES,
    note: 'The publisher reports 100 languages. Measured here on English and a few Russian messages; quality can be lower for languages with less training data.',
  },
  upstream: {
    repo: 'Xenova/multilingual-e5-small',
    revision: E5_REVISION,
    url: `https://huggingface.co/Xenova/multilingual-e5-small/resolve/${E5_REVISION}/`,
  },
  files: [
    { path: 'onnx/model_quantized.onnx', role: 'weights', bytes: 118_308_185, sha256: 'f80102d3f2a1229f387d3c81909990d8945513e347b0eab049f7de3c6f98c193' },
    { path: 'tokenizer.json', role: 'tokenizer', bytes: 17_082_730, sha256: '0b44a9d7b51c3c62626640cda0e2c2f70fdacdc25bbbd68038369d14ebdf4c39' },
    { path: 'tokenizer_config.json', role: 'tokenizer-config', bytes: 443, sha256: 'a1d6bc8734a6f635dc158508bef000f8e2e5a759c7d92f984b2c86e5ff53425b' },
  ],
  downloadBytes: 135_391_358,
  recipe: {
    dims: 384,
    maxTokens: 512,
    queryPrefix: 'query: ',
    passagePrefix: 'passage: ',
    pooling: 'mean',
    normalize: true,
    tokenizer: 'hf-tokenizers-json',
    bodyMaxChars: 1200,
  },
  calibration: {
    method: 'anchored-share',
    version: 1,
    temperature: 0.03,
    anchors: [...E5_ANCHORS],
    fewTextsBelow: 3,
    fewTextsBoost: 0.045,
  },
  memoryMb: 700,
  quality: {
    topicAuc: 0.935,
    topicAucNamesOnly: 0.867,
    secondsPerMessage: 0.35,
    measuredOn: '247 hand-labelled messages of the owner’s feeds, 2026-10-05, browser WASM, one thread',
    caveats: [
      'Measured on one reader’s 247 messages, 16 of them Cyrillic; the thresholds were chosen on the same messages, so expect somewhat worse.',
      'Deciding the folder is hard for every model: topic matching is the strong part, the ladder is a coarse sieve.',
      'Russian is its least-tested area: a Cyrillic message is compared with English topic phrases across languages.',
      'Topics with only a name match clearly worse (0.87 instead of 0.94): write a few short paraphrases.',
      'Uses about 0.7 GB of memory while it runs; it is unloaded after two idle minutes.',
    ],
  },
  topicFields: {
    supported: ['name', 'description', 'phrases'],
    default: ['name', 'phrases'],
    limits: { nameChars: 80, descriptionChars: 1200, phrases: 16, phraseChars: 120 },
  },
  defaults: {
    enabled: true,
    position: 1,
    run: E5_RECOMMENDED_RUN,
    options: { bodyMaxChars: 1200, topicMinScore: 20, dailyBudget: null, topicFields: ['name', 'phrases'] },
    ladder: [
      { ruleId: 'sr_e5_priority' as RuleId, name: 'On-device: strong match', icon: 'star' as IconName, minScore: 45, action: { type: 'move', folderId: folder('priority') } },
      { ruleId: 'sr_e5_weak' as RuleId, name: 'On-device: loose match', icon: 'signal' as IconName, minScore: 20, action: { type: 'move', folderId: folder('weak') } },
      { ruleId: 'sr_e5_dismissed' as RuleId, name: 'On-device: below the bar', icon: 'archive' as IconName, minScore: null, action: { type: 'move', folderId: folder('dismissed') } },
    ],
  },
};

export const MODEL_CATALOG: ModelCatalog = {
  version: 1,
  servedFrom: '/models/',
  entries: [JEV_CATALOG_ENTRY, E5_SMALL_CATALOG_ENTRY],
};

/** The catalogue entry of `id`, or null for an id this build does not know. */
export function catalogEntry(id: string, catalog: ModelCatalog = MODEL_CATALOG): ModelCatalogEntry | null {
  return catalog.entries.find((e) => e.id === id) ?? null;
}

/** The ids of the catalogue, in catalogue order. */
export function catalogIds(catalog: ModelCatalog = MODEL_CATALOG): ModelId[] {
  return catalog.entries.map((e) => e.id);
}

/** Where an on-device file is served on the app's own origin: `${servedFrom}${id}/${revision}/${path}`. */
export function modelFileUrl(entry: EmbeddingCatalogEntry, path: string, catalog: ModelCatalog = MODEL_CATALOG): string {
  return `${catalog.servedFrom}${entry.id}/${entry.upstream.revision}/${path}`;
}
